import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, LogIn, RotateCcw, X } from 'lucide-react'
import { CameraCaptureModal } from '@/components/capture/CameraCaptureModal'
import { PageReviewSheet, type PageReviewPayload } from '@/components/capture/PageReviewSheet'
import { usePageFromPaper } from '@/hooks/usePageFromPaper'
import { useCommitPage } from '@/hooks/useCommitPage'
import { isSessionExpired } from '@/lib/authErrors'
import { getAuthUser } from '@/lib/supabase'
import { readSampleIds, writeSampleIds } from '@/lib/firstWeek'
import { draftTargetFor, matchSourcesFor, mergePaperIntoDraft } from '@/lib/planning/paperIntoDraft'
import { readDraft, writeDraftAndAnnounce } from '@/lib/planning/sessionDraft'
import { useSupabaseTasks } from '@/hooks/useSupabaseTasks'
import { useDomain } from '@/hooks/useDomain'
import { useFamilyMembers } from '@/hooks/useFamilyMembers'
import { signedInMember } from '@/lib/paperAssignee'
import { useHouseholdSeasons } from '@/hooks/useHouseholdSeasons'
import { useGoalsContext } from '@/contexts/GoalsContext'
import { showToast } from '@/hooks/useToast'
import type { ExistingTask } from '@/lib/planDuplicates'
import type { DomainId } from '@/lib/domains'
import type { FamilyMember } from '@/types/family'
import type { PageAltitude } from '@/lib/planParse'

function rememberedDomain(altitude: PageAltitude): DomainId | undefined {
  try {
    const raw = localStorage.getItem(`symphony.paper.domain.${altitude}`)
    return raw === 'work' || raw === 'family' || raw === 'personal' ? raw : undefined
  } catch {
    return undefined
  }
}

interface PageFromPaperFlowProps {
  members: FamilyMember[]
  onClose: () => void
  /** Open (not completed) tasks, for the sheet's Link / Keep separate offer. */
  existingTasks?: ExistingTask[]
  /** 'YYYY-MM-DD' → the day's event titles, for day-facts already on the calendar. */
  calendarTitlesByDay?: Map<string, string[]>
  /** Skip the camera and parse this blob on mount instead — the first-week
   *  card's "Use our sample page" offer, which hands over the bundled sample
   *  image rather than making a paperless household photograph anything. */
  initialBlob?: Blob
  /** Altitude for `initialBlob`. Defaults to 'week'. Ignored without initialBlob. */
  initialAltitude?: PageAltitude
  /** The page being committed is the bundled sample, not a real one — the
   *  rows it creates are tracked (client-side) so "Clear sample" can remove
   *  them once a real page replaces the need for it. */
  sample?: boolean
  /** When the page was snapped. Defaults to now; a seam for tests. */
  today?: Date
}

/**
 * Page-from-paper, end to end: camera (or file) → parse → review → commit.
 * Mounted by HomeViewContainer when the Today overflow item is chosen; every
 * exit path lands on onClose so the mount fully resets between runs.
 *
 * Owns `useCommitPage()` rather than taking a commit callback. That hook drags
 * in a fresh `useSupabaseTasks` (its own realtime channel plus a full task
 * refetch), `useNotes`, and `useFamilyMembers` — held by HomeViewContainer it
 * cost every Today load a duplicate channel and duplicate fetches for a flow
 * that is open for a few seconds a week. This component mounts only while the
 * flow is open, so those hooks instantiate only then.
 */
export function PageFromPaperFlow({ members, onClose, existingTasks, calendarTitlesByDay, initialBlob, initialAltitude, sample, today }: PageFromPaperFlowProps) {
  const { status, result, error, parseFromBlob, parseFromStoragePath, retry, reset } = usePageFromPaper(members)
  const { commitPage } = useCommitPage()
  const navigate = useNavigate()
  const [userId, setUserId] = useState<string | null>(null)
  useEffect(() => { void getAuthUser().then(({ data: { user } }) => setUserId(user?.id ?? null)) }, [])
  // The lists a page's lines are matched against, exactly as the session
  // pages build them — so an import cannot add what the plan already holds.
  const { tasks } = useSupabaseTasks()
  const { layers } = useDomain()
  const { getCurrentUserMember, currentUserId } = useFamilyMembers()
  const { seasons } = useHouseholdSeasons()
  const { goals } = useGoalsContext()
  const meId = getCurrentUserMember()?.id ?? null
  const [camera, setCamera] = useState(!initialBlob)
  // Which page is being snapped. Chosen in the camera modal; the client owns
  // the window, so it must own the altitude too. Week = the old behaviour.
  const [altitude, setAltitude] = useState<PageAltitude>(initialAltitude ?? 'week')
  const [committing, setCommitting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // A bundled blob (the sample page) skips the camera entirely and parses on
  // mount, on whatever altitude the caller asked for.
  useEffect(() => {
    if (initialBlob) void parseFromBlob(initialBlob, initialAltitude ?? 'week')
    // Only ever runs for the mount this instance was given a blob for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const close = useCallback(() => {
    reset()
    onClose()
  }, [reset, onClose])

  const handleBlob = useCallback((blob: Blob) => {
    setCamera(false)
    void parseFromBlob(blob, altitude)
  }, [parseFromBlob, altitude])

  // The phone already uploaded the page; read it from where it landed.
  const handlePhoneHandoff = useCallback((storagePath: string) => {
    setCamera(false)
    void parseFromStoragePath(storagePath, altitude)
  }, [parseFromStoragePath, altitude])

  const handleFile = useCallback((file: File | null) => {
    if (file) handleBlob(file)
    else close()
  }, [handleBlob, close])
  // Closing the file picker without choosing fires no change event — only
  // `cancel`. Without this the flow stayed mounted and invisible, and the
  // button that opened it could not open it again (Scott, 2026-09-28).
  useEffect(() => {
    const input = fileInputRef.current
    if (!input) return
    const onCancel = () => close()
    input.addEventListener('cancel', onCancel)
    return () => input.removeEventListener('cancel', onCancel)
  }, [close])

  const handleCommit = useCallback(async (payload: PageReviewPayload) => {
    setCommitting(true)
    try {
      // The sheet asks which layer the page belongs to; the payload carries it.
      const { route, createdTaskIds, createdNoteIds } = await commitPage({ ...payload, storagePath: result.storagePath, altitude: result.altitude })
      if (sample && (createdTaskIds.length || createdNoteIds.length)) {
        const { data: { user } } = await getAuthUser()
        if (user) {
          const existing = readSampleIds(user.id)
          writeSampleIds(user.id, {
            taskIds: [...existing.taskIds, ...createdTaskIds],
            noteIds: [...existing.noteIds, ...createdNoteIds],
          })
        }
      }
      reset()
      onClose()
      navigate(route)
    } finally {
      setCommitting(false)
    }
  }, [commitPage, reset, onClose, navigate, result.storagePath, result.altitude, sample])

  // The plan being written right now that this page would join. The sheet's
  // month/season chip is the user saying which period the page is FOR, so it
  // decides the target; a chip moved to a period with no session in progress
  // has no offer at all.
  const targetFor = useCallback(
    (chosenStart?: Date | null) => draftTargetFor(result.altitude, today ?? new Date(), seasons, userId, chosenStart),
    [result.altitude, seasons, userId, today],
  )
  // "Add to the plan I'm writing": the page's goal and task lines join the
  // open draft — matched first, so nothing it already holds is added twice —
  // and everything else (day-facts, routines, notes) commits as usual.
  const handleAddToDraft = useCallback(async (payload: PageReviewPayload) => {
    const target = targetFor(payload.monthStart ?? payload.seasonStart ?? null)
    if (!target) return
    const draft = readDraft(userId, target.level, target.periodStart)
    if (!draft) return
    setCommitting(true)
    try {
      const merged = mergePaperIntoDraft(draft, payload, matchSourcesFor(target, { tasks, goals, layers, meId, seasons }))
      // Announced, so a session open on this draft in this tab re-reads it
      // instead of saving its older copy back over the import.
      writeDraftAndAnnounce(userId, merged.draft)
      // Always committed, even with nothing left over: that call is what pins
      // the page image to the period as an attachment.
      await commitPage({ ...merged.rest, storagePath: result.storagePath, altitude: result.altitude })
      showToast(`Added to the ${target.label} draft. Open it to review.`, 'success', 4000)
      reset()
      onClose()
    } finally {
      setCommitting(false)
    }
  }, [targetFor, userId, tasks, goals, layers, meId, seasons, commitPage, result.storagePath, result.altitude, reset, onClose])

  return (
    <>
      {/* Hidden file input — the camera modal's "pick a file" fallback. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        // Cleared after reading, so choosing the same file again still fires.
        onChange={(e) => { handleFile(e.target.files?.[0] ?? null); e.target.value = '' }}
      />

      {camera && (
        <CameraCaptureModal
          altitude={altitude}
          onAltitudeChange={setAltitude}
          onPhoneHandoff={handlePhoneHandoff}
          onCapture={handleBlob}
          onPickFile={() => {
            setCamera(false)
            fileInputRef.current?.click()
          }}
          onClose={close}
        />
      )}

      {status === 'parsing' && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4">
          <div className="bg-bg-elevated rounded-2xl shadow-2xl px-8 py-6 flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
            <span className="text-[15px] text-neutral-700">Reading your page…<span className="block text-[12.5px] text-neutral-500">This usually takes 20–30 seconds.</span></span>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-bg-elevated rounded-2xl shadow-2xl px-6 py-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <p className="text-[15px] text-neutral-800 mb-1">
              {isSessionExpired(error) ? 'Signed out' : 'Couldn’t read the page'}
            </p>
            <p className="text-[13px] text-neutral-500 mb-4 break-words">{error}</p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={close} className="px-4 py-2 rounded-lg text-[14px] text-neutral-600 hover:bg-neutral-100 transition-colors">
                <X className="w-4 h-4 inline mr-1" />Close
              </button>
              {isSessionExpired(error) ? (
                <button
                  type="button"
                  onClick={() => window.location.assign('/?return=' + encodeURIComponent(window.location.pathname))}
                  className="btn-primary px-4 py-2 rounded-lg text-[14px]"
                >
                  <LogIn className="w-4 h-4 inline mr-1" />Sign in again
                </button>
              ) : (
                <button type="button" onClick={() => void retry()} className="btn-primary px-4 py-2 rounded-lg text-[14px]">
                  <RotateCcw className="w-4 h-4 inline mr-1" />Try again
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {status === 'ready' && (
        <PageReviewSheet
          items={result.items}
          notes={result.notes}
          unclear={result.unclear}
          windowDates={result.windowDates}
          altitude={result.altitude}
          titlePeriod={result.titlePeriod}
          pageTitle={result.pageTitle}
          existingTasks={existingTasks}
          calendarTitlesByDay={calendarTitlesByDay}
          initialDomain={rememberedDomain(result.altitude)}
          {...(today ? { today } : {})}
          members={members}
          currentMemberId={signedInMember(members, currentUserId)?.id ?? null}
          committing={committing}
          onCommit={(payload) => void handleCommit(payload)}
          draftLabelFor={(chosenStart) => targetFor(chosenStart)?.label}
          onAddToDraft={(payload) => void handleAddToDraft(payload)}
          onClose={close}
        />
      )}
    </>
  )
}
